import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommercialOffer, Offeree, OffereeStatus, OfferItemType, DefaultTableType, CommercialOfferOffereeRespond } from '../../../../../shared/models/commercial-offer.model';
import { CommercialOffersService } from '../../../../../shared/services/commercial-offers.service';
import { CommercialOffersPdfService } from '../../../../../shared/services/commercial-offers-pdf.service';
import { AuthService } from '../../../../../shared/services/auth.service';

@Component({
  selector: 'app-offeree-page',
  templateUrl: './offeree-page.component.html',
  styleUrls: ['./offeree-page.component.scss']
})
export class OffereePageComponent implements OnInit {
  selectedOffer: CommercialOffer | null = null;
  loading = false;
  error: string | null = null;
  successMessage: string | null = null;
  commentText: string = '';
  currentUserId: string = '';
  sendingComment = false;
  currentOffereeId: string = '';
  acceptingOffer = false;
  rejectingOffer = false;
  collapsedSubgroups: { [key: string]: boolean } = {};
  collapsedGroupedSubgroups: { [key: string]: boolean } = {};
  currentVersion: string = '';
  offerres: Offeree[] = [];
  comments: any[] = [];
  selectedVersionIndex: number = 0;
  notes: string = '';

  constructor(
    private route: ActivatedRoute,
    private commercialOffersService: CommercialOffersService,
    private commercialOffersPdfService: CommercialOffersPdfService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    // Get current user ID
    this.authService.getCurrentUser().subscribe(user => {
      this.currentUserId = user.id;
    });

    this.route.params.subscribe(params => {
      const encoded = params['id'];
      if (encoded) {
        let offereeId = encoded;
        let version = '1';
        try {
          const decoded = atob(encoded);
          const parts = decoded.split(':');
          offereeId = parts[0];
          version = parts[1] || '1';
        } catch (e) {
          // fallback: treat as plain id
        }
        this.currentOffereeId = offereeId;
        this.currentVersion = version;
        this.selectedVersionIndex = 0;
        this.loadOffer(encoded, offereeId, version);
      }
    });
  }

  loadOffer(encodedId: string, offereeId: string, version: string): void {
    this.loading = false;
    this.error = null;

    console.log('Loading offer for offeree ID:', offereeId, 'version:', version);
    this.commercialOffersService.getOffereeOffer(encodedId).subscribe({
      next: (offer: CommercialOffer) => {
        console.log('Received offer from API:', offer);
        this.selectedOffer = offer;

        // Patch: ensure commercialOfferVersions[0].offerGroups exists for PDF export
        if (
          this.selectedOffer &&
          this.selectedOffer.offerGroups &&
          (
            !this.selectedOffer.commercialOfferVersions ||
            !Array.isArray(this.selectedOffer.commercialOfferVersions) ||
            this.selectedOffer.commercialOfferVersions.length === 0
          )
        ) {
          this.selectedOffer.commercialOfferVersions = [{
            offerGroups: this.selectedOffer.offerGroups,
            notes: '',
            comments: this.selectedOffer.comments || [],
            version: this.selectedOffer.version ? this.selectedOffer.version.toString() : '1',
            lastUpdate: this.selectedOffer.lastUpdate ? new Date(this.selectedOffer.lastUpdate) : new Date(),
            offerres: this.selectedOffer.offerres || []
          }];
        }

        this.offerres = offer.offerres || [];
        this.comments = offer.comments || [];
        this.notes = (offer as any).notes || '';
        this.loading = false;
      },
      error: (error: any) => {
        this.error = 'Failed to load offer';
        this.loading = false;
        console.error('Error loading offer:', error);
      }
    });
  }

  getTotalValue(): number {
    if (!this.selectedOffer) return 0;
    const groups = this.getOfferGroups(this.selectedOffer);
    if (!groups || !Array.isArray(groups)) return 0;
    return groups.reduce((total: number, group: any) => total + this.getGroupSubtotal(group), 0);
  }

  isMaterialGroup(group: any): boolean {
    if (!group || !group.name) {
      return false;
    }
    return group.name.toLowerCase().includes('material') ||
           (group.subOfferGroups && group.subOfferGroups.some((subGroup: any) =>
             subGroup.offerItems && subGroup.offerItems.some((item: any) => item.tableType === DefaultTableType.MATERIALS)
           )) ||
           (group.offerItems && group.offerItems.some((item: any) => item.tableType === DefaultTableType.MATERIALS));
  }

  getGroupSubtotal(group: any): number {
    if (!group) {
      return 0;
    }
    let total = 0;

    // Calculate subtotal from groupedSubOfferGroups (for materials table)
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
        if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
          groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
            total += this.getSubGroupSubtotal(subGroup);
          });
        }
      });
    }

    // Calculate subtotal from subgroups
    if (group.subOfferGroups && Array.isArray(group.subOfferGroups)) {
      total += group.subOfferGroups.reduce((subTotal: number, subGroup: any) => {
        return subTotal + this.getSubGroupSubtotal(subGroup);
      }, 0);
    }

    // Calculate subtotal from legacy offerItems (if any)
    if (group.offerItems && Array.isArray(group.offerItems)) {
      total += group.offerItems
        .filter((item: any) => item.type === OfferItemType.CUSTOMER)
        .reduce((subTotal: number, item: any) => {
          return subTotal + (item.totalPrice || item.total || 0);
        }, 0);
    }

    return total;
  }

  getSubGroupSubtotal(subGroup: any): number {
    if (!subGroup || !Array.isArray(subGroup.offerItems)) {
      return 0;
    }
    return subGroup.offerItems
      .filter((item: any) => item.type === OfferItemType.CUSTOMER)
      .reduce((total: number, item: any) => {
        return total + (item.totalPrice || item.total || 0);
      }, 0);
  }

  getGroupTotal(group: any): number {
    if (!this.isMaterialGroup(group)) {
      // For custom tables, sum all customer items in the flat list
      return this.getAllCustomerItemsFlat(group).reduce((sum, item) => sum + (item.totalPrice || item.total || 0), 0);
    }
    // For material groups, use the existing logic
    return this.getGroupSubtotal(group);
  }

  getFlattenedGroupItems(group: any): any[] {
    if (!group) {
      return [];
    }
    const flattenedItems: any[] = [];

    // Add items from subgroups
    if (group.subOfferGroups && Array.isArray(group.subOfferGroups)) {
      // First, collect all subgroups that have customer items
      const subgroupsWithCustomerItems = group.subOfferGroups.filter((subGroup: any) => {
        const items = subGroup.offerItems && Array.isArray(subGroup.offerItems)
          ? subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER)
          : [];
        return items.length > 0;
      });

      // Only show subtotals if there are multiple subgroups
      const showSubtotals = subgroupsWithCustomerItems.length > 1;

      // Only show subgroup headers for material groups
      const showSubgroupHeaders = this.isMaterialGroup(group);

      subgroupsWithCustomerItems.forEach((subGroup: any) => {
        const items = subGroup.offerItems || [];
        const customerItems = items.filter((item: any) => item.type === OfferItemType.CUSTOMER);

        // Add subgroup header only for material groups
        if (showSubgroupHeaders) {
          flattenedItems.push({
            isSubgroupHeader: true,
            subgroupName: subGroup.name,
            designation: '',
            description: '',
            quantity: '',
            unitPrice: 0,
            totalPrice: 0
          });
        }

        // Add only customer items (checked items)
        flattenedItems.push(...customerItems);

        // Add subgroup subtotal only if there are multiple subgroups
        if (showSubtotals) {
          flattenedItems.push({
            isSubgroupSubtotal: true,
            subgroupName: subGroup.name,
            designation: '',
            description: '',
            quantity: '',
            unitPrice: 0,
            totalPrice: this.getSubGroupSubtotal(subGroup)
          });
        }
      });
    }

    // Add legacy items if they exist (only customer items)
    if (group.offerItems && Array.isArray(group.offerItems)) {
      const customerItems = group.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER);
      flattenedItems.push(...customerItems);
    }

    return flattenedItems;
  }

  filterSelectedItems(items: any[]): any[] {
    return Array.isArray(items) ? items.filter(i => i.type === OfferItemType.CUSTOMER) : [];
  }

  exportSelectedOfferToPdf() {
    if (this.selectedOffer) {
      this.commercialOffersPdfService.exportOfferToPdf(this.selectedOffer, true);
    }
  }

  sendComment(): void {
    if (!this.commentText.trim() || !this.selectedOffer || !this.currentOffereeId) {
      return;
    }
    this.sendingComment = true;
    const commentData = {
      userId: this.currentOffereeId,
      text: this.commentText.trim()
    };
    const version = this.selectedOffer.commercialOfferVersions?.[this.selectedVersionIndex]?.version || this.currentVersion || this.selectedOffer.version?.toString() || '1';
    this.commercialOffersService.addComment(
      this.selectedOffer.bgModelId,
      this.selectedOffer.id,
      version,
      commentData
    ).subscribe({
      next: (comment) => {
        // Add the new comment to the offer's comments array
        if (this.comments) {
          this.comments.push(comment);
        }
        this.commentText = '';
        this.sendingComment = false;
      },
      error: (error) => {
        console.error('Error sending comment:', error);
        this.sendingComment = false;
      }
    });
  }

  acceptOffer(): void {
    if (!this.selectedOffer || !this.currentOffereeId) {
      return;
    }
    this.acceptingOffer = true;
    this.error = null;
    this.successMessage = null;
    const version = this.selectedOffer.commercialOfferVersions?.[this.selectedVersionIndex]?.version || this.currentVersion || this.selectedOffer.version?.toString() || '1';
    this.commercialOffersService.updateOffereeStatus(
      this.selectedOffer.bgModelId,
      this.selectedOffer.id,
      this.currentOffereeId,
      version,
      OffereeStatus.ACCEPTED
    ).subscribe({
      next: (updatedOfferee: Offeree) => {
        if (this.offerres) {
          const offereeIndex = this.offerres.findIndex((o: Offeree) => o.id === this.currentOffereeId);
          if (offereeIndex !== -1) {
            this.offerres[offereeIndex] = updatedOfferee;
          }
        }
        this.acceptingOffer = false;
        this.successMessage = 'Quote accepted successfully';
      },
      error: (error) => {
        console.error('Error accepting quote:', error);
        this.acceptingOffer = false;
        this.error = 'Failed to accept Quote. Please try again.';
      }
    });
  }

  rejectOffer(): void {
    if (!this.selectedOffer || !this.currentOffereeId) {
      return;
    }
    this.rejectingOffer = true;
    this.error = null;
    this.successMessage = null;
    const version = this.selectedOffer.commercialOfferVersions?.[this.selectedVersionIndex]?.version || this.currentVersion || this.selectedOffer.version?.toString() || '1';
    this.commercialOffersService.updateOffereeStatus(
      this.selectedOffer.bgModelId,
      this.selectedOffer.id,
      this.currentOffereeId,
      version,
      OffereeStatus.REJECTED
    ).subscribe({
      next: (updatedOfferee: Offeree) => {
        if (this.offerres) {
          const offereeIndex = this.offerres.findIndex((o: Offeree) => o.id === this.currentOffereeId);
          if (offereeIndex !== -1) {
            this.offerres[offereeIndex] = updatedOfferee;
          }
        }
        this.rejectingOffer = false;
        this.successMessage = 'Quote rejected successfully';
      },
      error: (error) => {
        console.error('Error rejecting quote:', error);
        this.rejectingOffer = false;
        this.error = 'Failed to reject quote. Please try again.';
      }
    });
  }

  hasOffereeMadeDecision(): boolean {
    if (!this.selectedOffer || !this.currentOffereeId || !this.offerres) {
      return false;
    }
    const currentOfferee = this.offerres.find((o: Offeree) => o.id === this.currentOffereeId);
    return currentOfferee ?
      (currentOfferee.status === OffereeStatus.ACCEPTED || currentOfferee.status === OffereeStatus.REJECTED) :
      false;
  }

  getCurrentOffereeStatus(): OffereeStatus | null {
    if (!this.selectedOffer || !this.currentOffereeId || !this.offerres) {
      return null;
    }
    const currentOfferee = this.offerres.find((o: Offeree) => o.id === this.currentOffereeId);
    return currentOfferee ? currentOfferee.status : null;
  }

  toggleSubgroupCollapse(group: any, subgroupName: string) {
    const key = this.getSubgroupKey(group, subgroupName);
    this.collapsedSubgroups[key] = !this.collapsedSubgroups[key];
  }

  isSubgroupCollapsed(group: any, subgroupName: string): boolean {
    const key = this.getSubgroupKey(group, subgroupName);
    return !!this.collapsedSubgroups[key];
  }

  getSubgroupKey(group: any, subgroupName: string): string {
    return `${group.name}__${subgroupName}`;
  }

  getCustomerItems(subGroup: any): any[] {
    if (!subGroup || !Array.isArray(subGroup.offerItems)) {
      return [];
    }
    return subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER);
  }

  getSubgroupsWithCustomerItems(group: any): any[] {
    if (!group || !Array.isArray(group.subOfferGroups)) {
      return [];
    }
    return group.subOfferGroups.filter((subGroup: any) => this.getCustomerItems(subGroup).length > 0);
  }

  // Check if a group has grouped subgroups structure
  hasGroupedSubOfferGroups(group: any): boolean {
    return group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups) &&
          group.groupedSubOfferGroups.length > 0;
  }

  // Get grouped subgroups with customer items
  getGroupedSubgroupsWithCustomerItems(group: any): any[] {
    if (!group || !Array.isArray(group.groupedSubOfferGroups)) {
      return [];
    }

    return group.groupedSubOfferGroups.filter((groupedSubGroup: any) => {
      return groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups) &&
             groupedSubGroup.subOfferGroups.some((subGroup: any) => this.getCustomerItems(subGroup).length > 0);
    });
  }

  // Collapsible functionality for grouped subgroups
  toggleGroupedSubgroupCollapse(group: any, groupedSubgroupName: string) {
    const key = this.getGroupedSubgroupKey(group, groupedSubgroupName);
    this.collapsedGroupedSubgroups[key] = !this.collapsedGroupedSubgroups[key];
  }

  isGroupedSubgroupCollapsed(group: any, groupedSubgroupName: string): boolean {
    const key = this.getGroupedSubgroupKey(group, groupedSubgroupName);
    return !!this.collapsedGroupedSubgroups[key];
  }

  getGroupedSubgroupKey(group: any, groupedSubgroupName: string): string {
    return `${group.name}__grouped__${groupedSubgroupName}`;
  }

  getGroupedSubgroupSubtotal(groupedSubGroup: any): number {
    if (!groupedSubGroup || !Array.isArray(groupedSubGroup.subOfferGroups)) {
      return 0;
    }

    return groupedSubGroup.subOfferGroups.reduce((total: number, subGroup: any) => {
      return total + this.getSubGroupSubtotal(subGroup);
    }, 0);
  }

  isCustomTable(group: any): boolean {
    return !this.isMaterialGroup(group) && this.hasGroupedSubOfferGroups(group);
  }

  getCustomTableItemsFromGroupedStructure(group: any): any[] {
    if (!group || !group.groupedSubOfferGroups || !Array.isArray(group.groupedSubOfferGroups)) {
      return [];
    }

    const items: any[] = [];
    group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
      if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
        groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
          if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
            const customerItems = subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER);
            items.push(...customerItems);
          }
        });
      }
    });

    return items;
  }

  getCustomTableItems(group: any): any[] {
    if (!group || !Array.isArray(group.offerItems)) {
      return [];
    }
    return group.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER);
  }

  getOfferGroups(offer: CommercialOffer): any[] {
    // Prefer commercialOfferVersions if present, else fallback to root-level offerGroups
    if (offer.commercialOfferVersions && offer.commercialOfferVersions[this.selectedVersionIndex]?.offerGroups) {
      return offer.commercialOfferVersions[this.selectedVersionIndex].offerGroups;
    }
    return offer.offerGroups || [];
  }

  getAllCustomerItemsFlat(group: any): any[] {
    let items: any[] = [];

    // From groupedSubOfferGroups
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
        if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
          groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
            if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
              items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
            }
          });
        }
      });
    }

    // From subOfferGroups
    if (group.subOfferGroups && Array.isArray(group.subOfferGroups)) {
      group.subOfferGroups.forEach((subGroup: any) => {
        if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
          items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
        }
      });
    }

    // From direct offerItems
    if (group.offerItems && Array.isArray(group.offerItems)) {
      items.push(...group.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
    }

    return items;
  }

  getAllMaterialCustomerItemsFlat(group: any): any[] {
    let items: any[] = [];

    // From groupedSubOfferGroups
    if (group.groupedSubOfferGroups && Array.isArray(group.groupedSubOfferGroups)) {
      group.groupedSubOfferGroups.forEach((groupedSubGroup: any) => {
        if (groupedSubGroup.subOfferGroups && Array.isArray(groupedSubGroup.subOfferGroups)) {
          groupedSubGroup.subOfferGroups.forEach((subGroup: any) => {
            if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
              items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
            }
          });
        }
      });
    }

    // From subOfferGroups
    if (group.subOfferGroups && Array.isArray(group.subOfferGroups)) {
      group.subOfferGroups.forEach((subGroup: any) => {
        if (subGroup.offerItems && Array.isArray(subGroup.offerItems)) {
          items.push(...subGroup.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
        }
      });
    }

    // From direct offerItems
    if (group.offerItems && Array.isArray(group.offerItems)) {
      items.push(...group.offerItems.filter((item: any) => item.type === OfferItemType.CUSTOMER));
    }

    return items;
  }

  getMaterialItemIndex(group: any, groupedSubGroup: any, subGroup: any, item: any): number {
    // Flatten all material customer items in the group
    const allItems = this.getAllMaterialCustomerItemsFlat(group);
    // Find the index of the current item (by reference)
    return allItems.findIndex((i: any) => i === item);
  }

  getOffereeEmailByUserId(userId: string): string {
    if (!this.offerres || !Array.isArray(this.offerres)) {
      return userId;
    }
    const offeree = this.offerres.find((o: Offeree) => o.id === userId);
    return offeree ? offeree.email : userId;
  }

  hasCustomerItems(group: any): boolean {
    if (!group) {
      return false;
    }

    // Check if there are any customer items in the group
    const customerItems = this.getAllCustomerItemsFlat(group);
    return customerItems.length > 0;
  }

  shouldHideGroupName(group: any): boolean {
    if (!group || !group.name) {
      return false;
    }

    // For custom tables (non-material groups), hide default category names
    if (!this.isMaterialGroup(group)) {
      const name = group.name.toLowerCase().trim();
      return name === 'no name' ||
             name === 'default' ||
             name === 'unnamed' ||
             name === '' ||
             name === 'no nane' ||
             name === 'untitled';
    }

    return false;
  }

  shouldHideSubgroupName(subgroup: any): boolean {
    if (!subgroup || !subgroup.name) {
      return false;
    }

    const name = subgroup.name.toLowerCase().trim();
    return name === 'no name' ||
           name === 'default' ||
           name === 'unnamed' ||
           name === '' ||
           name === 'no nane' ||
           name === 'untitled';
  }

}
